import React from "react";
import { ListStore } from "./types";
import {
  VirtualizedList,
  ExtendedListChildComponentProps,
} from "./VirtualizedList";
import { cn } from "@/lib/utils";
import { useInfiniteQueryWithScroll } from "@/stores/queries/useInfiniteQueryWithScroll";
import { ErrorState } from "../ErrorState";
import { EmptyState } from "./EmptyState";
import { EmptyStateProps } from "./EmptyState";
import { NewItemsNotification } from "../NewItemsNotification";

interface InfiniteScrollListProps {
  store: ListStore;
  renderItem: (props: ExtendedListChildComponentProps) => React.ReactElement;
  renderSkeleton: (
    props: ExtendedListChildComponentProps
  ) => React.ReactElement;
  className?: string;
  queryKey: string[];
  queryFn: (params: {
    pageSize?: number;
    startAfterId?: string;
    filters: Record<string, string>;
    sortBy: string | null;
    sortDirection: "asc" | "desc";
  }) => Promise<{ items: Array<{ [key: string]: any }>; hasMore: boolean; lastId?: string }>;
  pageSize?: number;
  emptyState?: EmptyStateProps;
  enablePolling?: boolean;
  refetchInterval?: number;
  // Selection mode props
  isSelectable?: boolean;
  selectedItemIds?: Set<string>;
  onItemSelect?: (item: any, allItems: any[]) => void;
  getItemId?: (item: any) => string;
}

export function InfiniteScrollList({
  store,
  renderItem,
  renderSkeleton,
  className,
  queryKey,
  queryFn,
  pageSize,
  emptyState,
  enablePolling = false,
  refetchInterval,
  isSelectable = false,
  selectedItemIds,
  onItemSelect,
  getItemId,
}: InfiniteScrollListProps) {
  const { 
    items, 
    isLoading, 
    error, 
    hasMore, 
    handleItemsRendered,
    newItemsCount,
    dismissNewItems,
  } = useInfiniteQueryWithScroll({
      queryKey,
      queryFn,
      filters: store.filters,
      sortBy: store.sortBy,
      sortDirection: store.sortDirection,
      pageSize,
      enablePolling,
      refetchInterval,
    });

  if (error) {
    return <ErrorState error={error} />;
  }

  if (items.length === 0 && !isLoading) {
    return <EmptyState {...emptyState} className="mt-16" />;
  }

  return (
    <div className={cn("h-full flex flex-col relative", className)}>
      <NewItemsNotification
        count={newItemsCount}
        onDismiss={dismissNewItems}
      />
      
      <div className="flex-1 min-h-0 pt-2">
        <VirtualizedList
          items={items}
          hasMore={hasMore}
          renderItem={renderItem}
          renderSkeleton={renderSkeleton}
          onItemsRendered={handleItemsRendered}
          isLoading={isLoading}
          isSelectable={isSelectable}
          selectedItemIds={selectedItemIds}
          onItemSelect={onItemSelect ? (item: any) => onItemSelect(item, items) : undefined}
          getItemId={getItemId}
        />
      </div>
    </div>
  );
}
